import React from 'react';
import PropTypes from 'prop-types';
import CurrencySelectField from 'pages/Dashboard/CurrencySelectField';
import StatisticsCard from 'pages/Dashboard/StatisticsCard';
import StatisticsGraph from 'pages/Dashboard/StatisticsGraph';
import currencyHelper from 'helpers/currencyHelper';
import moment from 'moment';
import styles from 'pages/Dashboard/styles';
import translate from 'components/hoc/translate';
import utils from 'helpers/utils';

class Statistics extends React.Component {
    constructor(props, context) {
        super(props, context);

        const { config: { currency: { system, supported } } } = context;

        this.currencies = supported;

        this.state = {
            data: props.data,
            selectedCurrency: system || null,
        };
    }

    shouldComponentUpdate(nextProps, nextState) {
        return (this.props.data !== nextProps.data) ||
            (this.state.selectedCurrency !== nextState.selectedCurrency);
    }

    /**
     * Format to the current local currency.
     *
     * @param {number} value
     */
    formatToLocalCurrency = (value) => {
        const { selectedCurrency } = this.state;

        const locale = currencyHelper.getLocaleByCurrency(selectedCurrency);

        return new Intl.NumberFormat(locale, {
            currency: selectedCurrency,
            minimumFractionDigits: 2,
            style: 'currency',
        }).format(value);
    }

    /**
     * Fetches the date from the data object and returns its values.
     *
     * @param {string} date - Specific date
     * @returns {Object}
     */
    bookingsForDate = (date) => {
        const { data, selectedCurrency } = this.state;

        const fetchedDate = utils.find(data, function (value) {
            return value.label === date;
        });

        const result = fetchedDate.values.find((val) => {
            return val.currency === selectedCurrency;
        });

        return {
            bookings: result ? result.amount : 0,
            value: result ? this.formatToLocalCurrency(result.total) : null,
        };
    }

    /**
     * Filters to the week and returns an object.
     *
     * @param {string} week
     * @param {string} year
     * @returns {Object}
     */
    bookingsWeeklyPeriod = (week, year) => {
        const { data, selectedCurrency } = this.state;
        let dates = data;

        const weekDates = dates.filter((value) => {
            const date = moment(value.label);

            return (date.format('W') === week) && (date.format('Y') === year);
        });

        let amount = 0;
        let total = 0;

        weekDates.forEach((value) => {
            const result = value.values.find((val) => {
                return val.currency === selectedCurrency;
            });

            amount += result.amount;
            total += result.total;
        });

        return {
            bookings: amount,
            value: this.formatToLocalCurrency(total),
            week: week,
        };
    }

    /**
     * Divides the dates into two groups and pass it to the chart.
     *
     * @returns {Object}
     */
    fillGraphData = () => {
        let dates = [
            ...this.state.data,
        ];

        if (!dates.length) {
            return;
        }

        const { daysInBetween, lastYear, today } = this.props;
        const { selectedCurrency } = this.state;

        /**
         * Remove the current day from the array.
         */
        dates.splice(utils.findIndex(dates, function (date) {
            return date.label === today;
        }), 1);

        /**
         * Remove the current day a year ago from the array.
         */
        dates.splice(utils.findIndex(dates, function (date) {
            return date.label === lastYear;
        }), 1);

        let rawSets = [];

        /**
         * We slice from the today till the amount of results we want to fetch.
         * Then we reverse the values in the arrays.
         * This gives us the opportunity to show it a chronological order.
         */
        rawSets.push(dates.slice(0, daysInBetween).reverse());
        rawSets.push(dates.slice(daysInBetween, daysInBetween + daysInBetween).reverse());

        let legend = [];
        let datasets = [];

        rawSets.forEach((rawSet, key) => {
            let dataset = [];

            rawSet.forEach((value) => {
                const result = value.values.find((val) => {
                    return val.currency === selectedCurrency;
                });

                if (key === 0) {
                    legend.push(value.label);
                }
                dataset.push(result.total);
            });

            datasets.push(dataset);
        });

        return {
            datasets: datasets,
            dates: legend,
        };
    }

    handleSelectedCurrency = (value) => {
        if (this.state.selectedCurrency === value) {
            return;
        }

        this.setState({
            selectedCurrency: value,
        });
    }

    render() {
        const { reference, translate } = this.props;
        const { selectedCurrency } = this.state;

        const lastWeek = moment().subtract(1, 'week');
        const label = translate(`resources.${ reference }.title`);

        return (
            <div>
                <div style={ styles.currencies }>
                    <CurrencySelectField
                        currencies={ this.currencies }
                        selectedCurrency={ selectedCurrency }
                        selectedValue={ (value) => {
                            this.handleSelectedCurrency(value);
                        } }
                    />
                </div>

                <div style={ styles.flex }>
                    <StatisticsCard
                        data={ this.bookingsForDate(moment().format('YYYY-MM-DD')) }
                        label={ label }
                        selectedCurrency={ selectedCurrency }
                        styles={ styles }
                        title={ translate('pages.dashboard.widgets.statistics_card.today') }
                    />

                    <StatisticsCard
                        data={ this.bookingsForDate(moment().subtract(1, 'day').format('YYYY-MM-DD')) }
                        label={ label }
                        selectedCurrency={ selectedCurrency }
                        styles={ styles }
                        title={ translate('pages.dashboard.widgets.statistics_card.yesterday') }
                    />

                    <StatisticsCard
                        data={ this.bookingsWeeklyPeriod(moment().format('W'), moment().format('YYYY')) }
                        label={ label }
                        selectedCurrency={ selectedCurrency }
                        styles={ styles }
                        title={ translate('pages.dashboard.widgets.statistics_card.current_week') }
                        weekLabel={ translate('pages.dashboard.widgets.statistics_card.week') }
                    />

                    <StatisticsCard
                        data={ this.bookingsWeeklyPeriod(lastWeek.format('W'), lastWeek.format('YYYY')) }
                        label={ label }
                        selectedCurrency={ selectedCurrency }
                        styles={ styles }
                        title={ translate('pages.dashboard.widgets.statistics_card.last_week') }
                        weekLabel={ translate('pages.dashboard.widgets.statistics_card.week') }
                    />
                </div>

                <div style={ styles.flex }>
                    <StatisticsGraph
                        dataSet={ this.fillGraphData() }
                        labelCurrentMonth={ translate('pages.dashboard.widgets.statistics_graph.current_month') }
                        labelPreviousYear={ translate('pages.dashboard.widgets.statistics_graph.previous_year') }
                        selectedCurrency={ selectedCurrency }
                        styles={ styles }
                        title={ label }
                    />
                </div>
            </div>
        );
    }
}

Statistics.defaultProps = {
    reference: 'bookings',
};

Statistics.contextTypes = {
    config: PropTypes.object,
};

Statistics.propTypes = {
    data: PropTypes.array.isRequired,
    daysInBetween: PropTypes.number,
    lastYear: PropTypes.string,
    reference: PropTypes.string,
    today: PropTypes.string,
    translate: PropTypes.func.isRequired,
};

export default translate(Statistics);



// WEBPACK FOOTER //
// ./src/pages/Dashboard/Statistics.js